import {
	styled,
	Text,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector, useStore } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { COLOR_TOKEN } from 'src/components/color-token';
import { isNumber, useLoaded, useTrackingManager } from 'src/feature-app';
import { useOneFormContext } from 'src/feature-app/OneForm';
import { TRANSITION_TIME } from 'src/globals';
import { ArrowDown } from 'src/icons-core-imports';
import { OneFormState, Steps } from 'src/types';

interface NavigationWrapperProps {
	fullScreen?: boolean;
	isLastScreen?: boolean;
	notALayer?: boolean;
	formTheme?: string;
	formMin?: boolean;
	position?: string;
}

const NavigationWrapper = styled.div<NavigationWrapperProps>`
	display: ${props =>
		props.notALayer
			? 'block'
			: props.fullScreen
			? 'none'
			: props.isLastScreen
			? 'none'
			: 'block'};
	position: ${props =>
		!props.notALayer
			? 'fixed'
			: props.position
			? props.position
			: 'relative'};
	bottom: 0;

	width: 100%;
	&:first-child {
		width: 100%;
	}

	@media screen and (min-width: 960px) {
		width: ${props => (props.notALayer ? '100%' : '960px')};
	}

	&.navigation-enter {
		transform: translate(0%, 50%);
		opacity: 0;
	}
	&.navigation-enter-active {
		transform: translate(0, 0);
		opacity: 1;
		transition: transform ${TRANSITION_TIME.REGULAR}ms,
			opacity ${TRANSITION_TIME.SLOW}ms ease-in;
	}
	&.navigation-enter-done {
	}
	&.navigation-exit {
		transform: translate(0, 0);
		opacity: 1;
	}
	&.navigation-exit-active {
		transform: translate(0, -100%);
		opacity: 0.01;
		transition: all ${TRANSITION_TIME.REGULAR}ms,
			opacity ${TRANSITION_TIME.SLOW}ms ease-in;
	}
`;

interface ProgressWrapperProps {
	notALayer?: boolean;
}

const ProgressWrapper = styled.div<ProgressWrapperProps>`
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: ${props => (props.notALayer ? 'var(--size-grid010)' : '187px')};

	@media all and (min-width: 560px) {
		width: ${props => (props.notALayer ? 'var(--size-grid013)' : '303px')};
	}
	@media all and (min-width: 960px) {
		width: ${props => (props.notALayer ? 'var(--size-grid008)' : '320px')};
	}
	@media all and (min-width: 1280px) {
		width: ${props => (props.notALayer ? 'var(--size-grid006)' : '320px')};
	}
	@media all and (min-width: 1920px) {
		width: ${props => (props.notALayer ? 'var(--size-grid005)' : '320px')};
	}
	@media all and (min-width: 2560px) {
		width: ${props => (props.notALayer ? 'var(--size-grid004)' : '320px')};
	}
`;

const BarWrapper = styled.div`
	width: 100%;
	position: relative;
`;

interface UpperBarProps {
	width?: number;
	formTheme?: string;
}
const UpperBar = styled.div<UpperBarProps>`
	position: absolute;
	width: ${props => `${props.width}%`};
	height: 2px;
	background-color: ${props => COLOR_TOKEN[props.formTheme]?.border.active};
	transition: all ${TRANSITION_TIME.FAST}ms;
`;

const LowerBar = styled.div<UpperBarProps>`
	position: absolute;
	width: 100%;
	height: 2px;
	background-color: ${props => COLOR_TOKEN[props.formTheme]?.border.disabled};
`;

const TextWrapper = styled.div`
	align-self: flex-end;
	margin-top: 8px;
`;

const InnerWrapper = styled.div<NavigationWrapperProps>`
	display: flex;
	justify-content: space-between;
	align-items: ${props => (props.notALayer ? 'flex-end' : null)};
	background-color: ${props =>
		props.formTheme == 'main'
			? COLOR_TOKEN.main.background.primary
			: props.notALayer
			? COLOR_TOKEN.main.background.primary
			: COLOR_TOKEN.inverted.background.primary};

	width: 100%;
	padding-bottom: ${props =>
		props.notALayer
			? '16px'
			: props.fullScreen
			? '0'
			: props.isLastScreen
			? '0'
			: '20px'};
	padding-left: var(--size-grid002);
	padding-right: ${props =>
		props.notALayer ? 'var(--size-grid002)' : 'var(--size-grid001)'};

	@media screen and (min-width: 560px) {
		padding-bottom: ${props =>
			props.notALayer
				? '16px'
				: props.fullScreen
				? '0'
				: props.isLastScreen
				? '0'
				: '24px'};
	}
	@media screen and (min-width: 960px) {
		padding-left: ${props =>
			props.notALayer ? 'var(--size-grid003)' : '120px'};
		padding-right: ${props =>
			props.notALayer ? 'var(--size-grid003)' : '40px'};
		padding-bottom: ${props =>
			props.notALayer
				? '16px'
				: props.fullScreen
				? '0'
				: props.isLastScreen
				? '0'
				: '44px'};
	}
	@media screen and (min-width: 1280px) {
		padding-left: ${props =>
			props.notALayer ? 'var(--size-grid005)' : '120px'};
		padding-right: ${props =>
			props.notALayer ? 'var(--size-grid005)' : '40px'};
	}
	@media screen and (min-width: 1920px) {
		padding-left: ${props =>
			props.notALayer ? 'var(--size-grid006)' : '120px'};
		padding-right: ${props =>
			props.notALayer ? 'var(--size-grid006)' : '40px'};
	}
	@media screen and (min-width: 2560px) {
		padding-left: ${props =>
			props.notALayer ? 'var(--size-grid007)' : '120px'};
		padding-right: ${props =>
			props.notALayer ? 'var(--size-grid007)' : '40px'};
	}
`;

const BackgroundPositionWrapper = styled.div<ProgressWrapperProps>`
	position: ${props => (props.notALayer ? 'relative' : null)};
	width: 100%;
`;

const BackgroundColorNotALayer = styled.div<NavigationWrapperProps>`
	position: absolute;
	bottom: 0;
	width: 100%;
	background-color: ${COLOR_TOKEN.main.background.primary};
	border-top: ${props =>
		props.position === 'fixed'
			? `1px solid ${COLOR_TOKEN.main.background.highlight}`
			: null};
	height: 56px;
	z-index: -1;
`;

const NextButtonWrapper = styled.div<ProgressWrapperProps>`
	padding-bottom: ${props => (props.notALayer ? '8px' : null)};
`;

const NextButton = styled.button<{ isValid: boolean; formTheme: string }>`
	height: 64px;
	width: 64px;
	border-radius: 50%;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;

	background-color: ${props =>
		props.isValid
			? COLOR_TOKEN[props.formTheme]?.button.primary.active
			: COLOR_TOKEN[props.formTheme]?.button.primary.disabled};

	svg {
		color: ${props =>
			props.isValid
				? COLOR_TOKEN[props.formTheme]?.content.buttonActive
				: COLOR_TOKEN[props.formTheme]?.content.disabled};
	}
`;

function InnerNavigation(props: any) {
	const {
		formClicked,
		setFormClicked,
		notALayer,
		formTheme,
		soloProgreso,
		isFullScreen,
		isLastScreen,
		position,
	} = props;
	const { formInfo, formData } = useSelector((state: OneFormState) => state);
	const { fields } = formData;
	const { screenIndex, numberOfScreens, multiStepScreenIndex, step, steps } =
		useSelector((state: OneFormState) => state.formInfo);
	const { getValues, errors, triggerValidation, clearError } =
		useFormContext();
	const store = useStore();
	const {
		handleNextStep,
		setShowPreviousStep,
		isValueSelected,
		setIsValueSelected,
		setError,
	} = useOneFormContext();
	const [isValid, setIsValid] = useState<boolean>(false);
	const trackingManager = useTrackingManager();

	const checkIfValid = () => {
		const visibleInputs = document.querySelectorAll(
			'input:not([type="hidden"]):not([type="button"]), select, textarea',
		);

		if (visibleInputs.length === 0) {
			setIsValid(false);
			return;
		}

		let allValid = true;

		visibleInputs.forEach((input: any) => {
			const isVisible = input.offsetParent !== null;
			const isRequired = input.hasAttribute('required');
			const hasValue = input.value && input.value.trim() !== '';

			if (isVisible && isRequired && !hasValue) {
				allValid = false;
			}
			// Si el steps solo tiene un input, lo cosideramos required
			if (isVisible && !hasValue && visibleInputs.length === 1) {
				allValid = false;
			}
		});

		if (allValid) {
			setIsValid(true);
			setError(false);
		} else {
			setIsValid(false);
		}
	};

	useEffect(() => {
		const checkValidity = () => checkIfValid();
		if (formClicked || isValueSelected) {
			checkValidity();
			if (formClicked) setFormClicked(false);
			if (isValueSelected) setIsValueSelected(false);
		}
	}, [formClicked, isValueSelected]);

	useEffect(() => {
		const handleInputChange = () => {
			setTimeout(() => checkIfValid(), 100);
		};

		document.addEventListener('input', handleInputChange);
		document.addEventListener('change', handleInputChange);

		return () => {
			document.removeEventListener('input', handleInputChange);
			document.removeEventListener('change', handleInputChange);
		};
	}, []);

	useEffect(() => {
		setTimeout(() => {
			if (steps) {
				const currentStep = steps.find(
					step => step.screenIndex === screenIndex,
				);
				const { notALayer } = store.getState().formInfo;
				if (
					!notALayer &&
					((screenIndex === 0 && !steps[screenIndex].multiStep) ||
						screenIndex === numberOfScreens - 1 ||
						(currentStep && currentStep.hidePreviousStep) ||
						screenIndex > numberOfScreens)
				) {
					setShowPreviousStep(false);
				} else {
					setShowPreviousStep(true);
				}
			}
		}, 100);

		setTimeout(() => {
			checkIfValid();
		}, 600);
	}, [screenIndex]);

	useEffect(() => {
		const {
			step,
			multiSteps,
			multiStepScreenIndex,
		}: { step: Steps; multiSteps: Steps[]; multiStepScreenIndex: number } =
			store.getState().formInfo;

		setTimeout(() => {
			if (
				multiSteps &&
				isNumber(multiStepScreenIndex) &&
				step &&
				step.multiStep
			) {
				if (multiSteps[multiStepScreenIndex].hidePreviousStep) {
					setShowPreviousStep(false);
				} else {
					setShowPreviousStep(true);
				}
			} else {
				setShowPreviousStep(true);
			}
		}, 0);

		setTimeout(() => {
			checkIfValid();
		}, 600);
	}, [multiStepScreenIndex]);

	const getPercentage = (): string => {
		const percentage = (
			(screenIndex / (numberOfScreens - 1)) *
			100
		).toFixed(0);
		if (numberOfScreens - 1 == 1) {
			return '50';
		} else if (percentage === '0') {
			return '0';
		} else {
			return percentage;
		}
	};

	return (
		<BackgroundPositionWrapper notALayer={notALayer}>
			{notALayer ? (
				<BackgroundColorNotALayer
					className="background-color"
					position={position}
				/>
			) : null}
			<InnerWrapper
				className="navigation-wrapper"
				notALayer={notALayer}
				fullScreen={isFullScreen}
				isLastScreen={isLastScreen}
			>
				<ProgressWrapper notALayer={notALayer}>
					<BarWrapper>
						<LowerBar formTheme={formTheme || 'main'} />
						<UpperBar
							width={parseFloat(getPercentage())}
							formTheme={formTheme || 'main'}
						/>
					</BarWrapper>
					<TextWrapper>
						<Text
							color={TokenTextColor.primary}
							appearance={TokenTextAppearance.copy100}
						>
							{getPercentage()}% completado
						</Text>
					</TextWrapper>
				</ProgressWrapper>
				{soloProgreso ? null : (
					<NextButtonWrapper notALayer={notALayer}>
						<NextButton
							onClick={async (e: any) => {
								trackingManager.trackFormStepButtonClick({
									contentId: 'Siguiente',
								});
								handleNextStep(e);
							}}
							isValid={isValid}
							formTheme={formTheme || 'main'}
						>
							<ArrowDown />
						</NextButton>
					</NextButtonWrapper>
				)}
			</InnerWrapper>
		</BackgroundPositionWrapper>
	);
}

interface NavigationProps {
	formClicked?: boolean;
	setFormClicked?: React.Dispatch<React.SetStateAction<boolean>>;
	notALayer?: boolean;
	formTheme?: string;
	refProps?: any;
	formMin?: boolean;
	soloProgreso: boolean;
	position?: string;
}

export function Navigation(props: NavigationProps) {
	const { formTheme, notALayer, position } = props;
	const { screenIndex, step } = useSelector(
		(state: OneFormState) => state.formInfo,
	);
	const loaded = useLoaded();
	const [isLastScreen, setIsLastScreen] = useState(false);
	const { isFullScreen } = useOneFormContext();
	const store = useStore();

	useEffect(() => {
		const { formInfo } = store.getState();
		const { step, lastStep } = formInfo;
		if (step && lastStep) {
			step.screenIndex === lastStep.screenIndex
				? setIsLastScreen(true)
				: setIsLastScreen(false);
		}
	}, [step]);

	return (
		<CSSTransition timeout={500} in={loaded} classNames="navigation">
			<NavigationWrapper
				fullScreen={isFullScreen}
				isLastScreen={isLastScreen}
				className="navigation-wrapper"
				notALayer={notALayer}
				formTheme={formTheme}
				position={position}
			>
				{isLastScreen ? null : <InnerNavigation {...props} />}
			</NavigationWrapper>
		</CSSTransition>
	);
}
